import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/hooks/use-toast';

export interface FeatureToggle {
  id: string;
  feature_key: string;
  feature_name: string;
  description: string | null;
  enabled_for_roles: string[];
  is_active: boolean;
  created_at: string;
  updated_at: string;
  created_by: string | null;
  updated_by: string | null;
}

// Helper to safely parse enabled_for_roles from JSON
const parseEnabledRoles = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value.filter((v): v is string => typeof v === 'string');
  }
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) {
        return parsed.filter((v): v is string => typeof v === 'string');
      }
    } catch {
      return [];
    }
  }
  return [];
};

export const useFeatureToggles = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: toggles, isLoading } = useQuery({
    queryKey: ['feature-toggles'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('feature_toggles' as any)
        .select('*')
        .eq('is_active', true)
        .order('feature_name');

      if (error) throw error;
      
      // Parse enabled_for_roles for each toggle
      return ((data || []) as any[]).map(toggle => ({
        ...toggle,
        enabled_for_roles: parseEnabledRoles(toggle.enabled_for_roles),
      })) as FeatureToggle[];
    },
  });

  const updateToggleMutation = useMutation({
    mutationFn: async ({
      id,
      enabled_for_roles,
    }: {
      id: string;
      enabled_for_roles: string[];
    }) => {
      const { data, error } = await supabase
        .from('feature_toggles' as any)
        .update({
          enabled_for_roles,
          updated_by: user?.id,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)
        .select('id');

      if (error) throw error;
      if (!data || data.length === 0) {
        throw new Error(
          'Update was blocked by access rules. Only super_admin or program_director can change feature toggles.'
        );
      }
    },
    onMutate: async ({ id, enabled_for_roles }) => {
      await queryClient.cancelQueries({ queryKey: ['feature-toggles'] });
      const previous = queryClient.getQueryData<FeatureToggle[]>(['feature-toggles']);
      queryClient.setQueryData<FeatureToggle[]>(['feature-toggles'], (old) =>
        (old || []).map((t) => (t.id === id ? { ...t, enabled_for_roles } : t))
      );
      return { previous };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feature-toggles'] });
    },
    onError: (error: any, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(['feature-toggles'], context.previous);
      }
      toast({
        title: 'Error',
        description: error?.message || 'Failed to update feature toggle',
        variant: 'destructive',
      });
    },
  });

  const isFeatureEnabled = (featureKey: string, userRole?: string): boolean => {
    const roleToCheck = userRole || user?.role;
    if (!roleToCheck || !toggles) return false;

    const toggle = toggles.find(t => t.feature_key === featureKey);
    if (!toggle || !toggle.is_active) return false;

    return toggle.enabled_for_roles.includes(roleToCheck);
  };

  const canManageToggles = user?.role === 'super_admin' || user?.role === 'program_director';

  return {
    toggles,
    isLoading,
    isFeatureEnabled,
    updateToggle: updateToggleMutation.mutate,
    canManageToggles,
  };
};
